// Axios interceptors: serve cached GETs immediately, queue mutations, write-through when a request lands.
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { isOnline, isNetworkError, markReachable, markUnreachable } from "./network";
import {
  cacheGetUrl,
  cacheGetEntity,
  writeThroughResponse,
  guessSliceFromUrl,
  cacheSetCurrentUser,
  cacheGetCurrentUser,
  cacheSetEntity,
  cacheRemoveEntity,
} from "./cache";
import { enqueueMutation, offlineStatusSnapshot } from "./outbox";
import {
  normalizeUrl,
  isAuthUrl,
  serializeRequestBody,
  buildOptimisticEntity,
  extractIdFromUrl,
} from "./bodySerialize";
import { ensureNotificationPermission } from "./notifications";
import { patchOfflineStatus } from "./status";
import { runSync } from "./sync";
import { applyFetchedDataToStore } from "./hydrate";
import { GET_CURRENT_USER } from "modules/current_user/actions";

let installed = false;
let storeRef = null;
const inflightRefresh = new Set();

const DEFAULT_TIMEOUT_MS = 15000;

export const setOfflineAxiosStore = (store) => {
  storeRef = store;
};

const axiosResponse = (config, data, status = 200) => ({
  data,
  status,
  statusText: status === 200 ? "OK" : "Error",
  headers: {},
  config,
  request: {},
});

const refreshPending = async () => {
  if (!storeRef) return;
  storeRef.dispatch(patchOfflineStatus({ ...(await offlineStatusSnapshot()), online: isOnline() }));
};

const applyCurrentProjectLocally = async (serialized) => {
  const user = storeRef?.getState()?.current_user || (await cacheGetCurrentUser()) || {};
  const projectId = serialized?.bodyJson?.current_project_id;
  if (projectId == null) return user;
  const next = { ...user, current_project_id: Number(projectId) };
  await cacheSetCurrentUser(next);
  storeRef?.dispatch({ type: `${GET_CURRENT_USER}_FULFILLED`, payload: { data: next } });
  return next;
};

const handleOfflineMutation = async (config) => {
  const url = normalizeUrl(config);
  const method = _.toLower(config.method || "get");
  const slice = guessSliceFromUrl(url);
  const serialized = await serializeRequestBody(config.data);

  if (_.includes(url, "/current_project")) {
    const nextUser = await applyCurrentProjectLocally(serialized);
    await enqueueMutation({
      method,
      url,
      headers: _.pick(config.headers || {}, ["Content-Type"]),
      entitySlice: "current_user",
      ...serialized,
    });
    await refreshPending();
    if (isOnline()) setTimeout(() => runSync(), 0);
    return axiosResponse(config, nextUser);
  }

  let clientTempId = null;
  let entityId = extractIdFromUrl(url);
  let baseUpdatedAt = null;
  let cachedEntity = null;

  if (entityId != null && slice) {
    cachedEntity = await cacheGetEntity(slice, entityId);
    baseUpdatedAt = cachedEntity?.updated_at || null;
  }

  if (method === "post") {
    clientTempId = uuidv4();
    entityId = clientTempId;
  }

  const optimistic = buildOptimisticEntity({ method, url, clientTempId, cachedEntity, serialized });

  // FormData must not force Content-Type (boundary is set by the runtime)
  const headers =
    serialized.bodyKind === "formData" ? {} : _.pick(config.headers || {}, ["Content-Type"]);

  await enqueueMutation({
    method,
    url,
    headers,
    entitySlice: slice,
    entityId,
    clientTempId,
    baseUpdatedAt,
    ...serialized,
  });

  if (method === "delete") {
    if (slice && entityId != null) {
      await cacheRemoveEntity(slice, entityId);
      storeRef?.dispatch({ type: "offline_entity_removed", payload: { slice, id: entityId } });
    }
  } else if (slice && optimistic.id != null) {
    await cacheSetEntity(slice, optimistic);
    storeRef?.dispatch({ type: "offline_entity_replaced", payload: { slice, data: optimistic } });
  }

  await ensureNotificationPermission();
  await refreshPending();

  if (isOnline()) {
    setTimeout(() => runSync(), 0);
  }

  if (method === "delete") {
    return axiosResponse(config, { id: entityId });
  }
  return axiosResponse(config, optimistic);
};

const refreshGetInBackground = (url, config) => {
  if (!url || !isOnline() || inflightRefresh.has(url)) return;
  inflightRefresh.add(url);
  axios
    .get(url, {
      params: config.params,
      headers: config.headers,
      skipOffline: true,
      timeout: config.timeout || DEFAULT_TIMEOUT_MS,
    })
    .then(async (res) => {
      markReachable();
      await writeThroughResponse("get", url, res.data);
      applyFetchedDataToStore(storeRef, url, res.data);
    })
    .catch((e) => {
      if (isNetworkError(e)) markUnreachable();
    })
    .finally(() => inflightRefresh.delete(url));
};

export const installOfflineInterceptors = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use(async (config) => {
    if (config.skipOffline) return config;

    if (!config.timeout) config.timeout = DEFAULT_TIMEOUT_MS;

    const method = _.toLower(config.method || "get");
    const url = normalizeUrl(config);

    if (isAuthUrl(url)) return config;

    if (method === "get") {
      const cached = await cacheGetUrl("GET", url);
      if (cached) {
        if (isOnline()) refreshGetInBackground(url, config);
        config.adapter = async () => axiosResponse(config, cached.data);
      } else if (!isOnline()) {
        config.adapter = async () => {
          const error = new Error("Not available offline");
          error.config = config;
          error.response = axiosResponse(config, { error: "Not available offline" }, 503);
          error.isAxiosError = true;
          return Promise.reject(error);
        };
      }
    } else if (["post", "patch", "put", "delete"].includes(method)) {
      // Always local-first: flaky towers shouldn't block a save. Sync drains when reachable.
      config.adapter = async () => handleOfflineMutation(config);
    }

    return config;
  });

  axios.interceptors.response.use(
    async (response) => {
      markReachable();
      if (response.config?.skipOffline || response.config?.skipOfflineWrite) return response;
      const method = _.toLower(response.config?.method || "get");
      const url = normalizeUrl(response.config);
      if (isAuthUrl(url)) {
        if (_.includes(url, "/auth/me") && response.data) {
          await cacheSetCurrentUser(response.data);
        }
        return response;
      }
      if (["get", "post", "patch", "put"].includes(method) && response.data != null) {
        await writeThroughResponse(method, url, response.data);
      }
      return response;
    },
    async (error) => {
      const config = error?.config;
      if (isNetworkError(error)) markUnreachable();
      if (!config || config.skipOffline) return Promise.reject(error);

      const method = _.toLower(config.method || "get");
      const url = normalizeUrl(config);
      const networkFail = isNetworkError(error);

      if (method === "get" && networkFail && !isAuthUrl(url)) {
        const cached = await cacheGetUrl("GET", url);
        if (cached) {
          return axiosResponse(config, cached.data);
        }
      }

      if (networkFail && ["post", "patch", "put", "delete"].includes(method) && !isAuthUrl(url)) {
        try {
          return await handleOfflineMutation(config);
        } catch (_e) {
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
};
